import { Link, usePathname } from 'expo-router';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { signOut } from '@/lib/auth';
import { palette, useTheme } from '@/theme';

type NavItem = { href: string; label: string; key: string; adminOnly?: boolean };

export function AdminSidebar({ role, displayName }: { role: 'admin' | 'teacher' | null; displayName?: string }) {
  const theme = useTheme();
  const pathname = usePathname();

  const items: NavItem[] = [
    { href: '/admin', key: 'overview', label: 'Übersicht' },
    { href: '/admin/kurse', key: 'kurse', label: 'Kurse & Module' },
    { href: '/admin/klienten', key: 'clients', label: 'Klienten' },
    { href: '/admin/lehrer', key: 'teachers', label: 'Lehrer', adminOnly: true },
    { href: '/admin/finanzen', key: 'finance', label: 'Finanzen', adminOnly: true },
    { href: '/admin/kommunikation', key: 'comms', label: 'Kommunikation' },
  ];

  const visible = items.filter((it) => !it.adminOnly || role === 'admin');

  return (
    <View style={[styles.outer, { backgroundColor: theme.colors.fg }]}>
      <View style={styles.logo}>
        <Text style={{ fontFamily: theme.fontFamily.display, fontSize: 28, color: palette.paper }}>
          Zouk<Text style={{ fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic', color: palette.emberSoft }}>ess</Text>ence
        </Text>
        <Text variant="meta" style={{ color: 'rgba(245,240,255,0.6)', marginTop: 4 }}>
          Admin
        </Text>
      </View>

      <View style={styles.nav}>
        {visible.map((it) => {
          const active = pathname === it.href || (it.href !== '/admin' && pathname.startsWith(it.href));
          return (
            <Link key={it.key} href={it.href as any} asChild>
              <Pressable
                accessibilityRole="link"
                style={StyleSheet.flatten([
                  styles.navItem,
                  active && { backgroundColor: 'rgba(245,240,255,0.08)' },
                  { borderRadius: theme.radii.md },
                ])}
              >
                <Text
                  style={{
                    color: active ? palette.paper : 'rgba(245,240,255,0.7)',
                    fontFamily: theme.fontFamily.bodyMedium,
                    fontSize: 14,
                  }}
                >
                  {it.label}
                </Text>
              </Pressable>
            </Link>
          );
        })}
      </View>

      <View style={[styles.foot, { borderTopColor: 'rgba(245,240,255,0.14)' }]}>
        <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.5)' }}>Angemeldet</Text>
        <Text style={{ color: palette.paper, marginTop: 6 }}>{displayName ?? '—'}</Text>
        <Text variant="meta" style={{ color: 'rgba(245,240,255,0.5)', marginTop: 4 }}>
          {role === 'admin' ? 'Admin' : role === 'teacher' ? 'Lehrperson · limited' : 'Gast'}
        </Text>
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            void signOut();
          }}
          style={[styles.logout, { borderColor: 'rgba(245,240,255,0.16)' }]}
        >
          <Text variant="meta" style={{ color: palette.emberSoft }}>Abmelden</Text>
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outer: {
    width: 260,
    padding: 24,
    minHeight: '100%',
  },
  logo: {
    marginBottom: 48,
  },
  nav: {
    flexGrow: 1,
    gap: 4,
  },
  navItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  foot: {
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
  },
  logout: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 999,
    alignSelf: 'flex-start',
  },
});
